/**
 * @fileoverview Modal dialog for displaying informational text with an OK button.
 * @module ui/InformationDialog
 */

import { BaseDialog } from './BaseDialog.js';
import { escapeHtml } from '../utils/jrfuncs.js';


/**
 * Allowlist of HTML tags and their permitted attributes for sanitization.
 * Everything not listed here is stripped out.
 * @type {Object<string, string[]>}
 */
const SAFE_TAGS = {
    // target/rel are not copied from the source: sanitizeNode always writes its own
    // target="_blank" rel="noopener noreferrer", and a copied pair would come first and win
    a:      ['href', 'title'],
    b:      [],
    i:      [],
    em:     [],
    strong: [],
    br:     [],
    p:      [],
    ul:     [],
    ol:     [],
    li:     [],
};


/**
 * Sanitizes an HTML string, keeping only allowlisted tags and attributes.
 * Links are forced to open in a new tab with noopener/noreferrer.
 * Only http, https, and mailto URLs are permitted on href attributes.
 * All other tags and attributes are stripped; text content is escaped.
 *
 * @param {string} dirty - Untrusted HTML string
 * @returns {string} Sanitized HTML safe for innerHTML
 */
export function sanitizeHtml(dirty) {
    const doc = new DOMParser().parseFromString(dirty, 'text/html');
    return sanitizeNode(doc.body);
}


/**
 * Recursively walks a DOM node tree and produces sanitized HTML.
 * @param {Node} node - The DOM node to process
 * @returns {string} Sanitized HTML string
 * @private
 */
function sanitizeNode(node) {
    let out = '';

    for (const child of node.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
            out += escapeHtml(child.textContent, false);
            continue;
        }

        if (child.nodeType !== Node.ELEMENT_NODE) {
            continue;
        }

        const tag = child.tagName.toLowerCase();
        const allowedAttrs = SAFE_TAGS[tag];

        if (allowedAttrs === undefined) {
            // Tag not in allowlist — process children only (strip the tag itself)
            out += sanitizeNode(child);
            continue;
        }

        // Build the opening tag with only allowed attributes
        let attrs = '';
        for (const attrName of allowedAttrs) {
            const val = child.getAttribute(attrName);
            if (val == null) continue;

            // For href, only allow safe URL schemes
            if (attrName === 'href') {
                const trimmed = val.trim().toLowerCase();
                if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://') && !trimmed.startsWith('mailto:')) {
                    continue;
                }
            }

            attrs += ` ${attrName}="${escapeHtml(val, false)}"`;
        }

        // Force links to open safely in a new tab
        if (tag === 'a') {
            attrs += ' target="_blank" rel="noopener noreferrer"';
        }

        // Self-closing tags
        if (tag === 'br') {
            out += '<br>';
            continue;
        }

        out += `<${tag}${attrs}>`;
        out += sanitizeNode(child);
        out += `</${tag}>`;
    }

    return out;
}


/**
 * Modal dialog for displaying a block of informational text.
 * Shows a title, text content, and an OK button to close.
 * Supports an optional htmlUnsafe parameter whose HTML is sanitized
 * to allow only safe tags (links, basic formatting) before rendering.
 * @class
 * @extends BaseDialog
 */
export class InformationDialog extends BaseDialog {

    /**
     * Creates a new InformationDialog instance.
     * @param {Object} [options={}] - Configuration options
     * @param {string} [options.title='Information'] - Modal title
     * @param {string} [options.text=''] - Plain text content to display (escaped)
     * @param {string} [options.htmlUnsafe=''] - Untrusted HTML to sanitize and display (links allowed)
     * @param {string} [options.icon='bi-info-circle'] - Bootstrap icon class for the title
     */
    constructor(options = {}) {
        super({ idPrefix: 'informationDialog' });

        this.options = {
            title: options.title || 'Information',
            text: options.text || '',
            htmlUnsafe: options.htmlUnsafe || '',
            icon: options.icon || 'bi-info-circle'
        };
    }


    /**
     * Returns the HTML for the information modal.
     * If htmlUnsafe is provided it is sanitized and used; otherwise text is escaped.
     * Uses buildModalHtml template from BaseDialog.
     * @returns {string} Modal HTML string
     * @override
     */
    getModalHtml() {
        let contentHtml;
        if (this.options.htmlUnsafe) {
            contentHtml = sanitizeHtml(this.options.htmlUnsafe);
        } else {
            contentHtml = escapeHtml(this.options.text, false);
        }

        const bodyHtml = `
            <div class="information-dialog-content" style="white-space: pre-wrap;">${contentHtml}</div>
        `;

        const footerHtml = `
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
                OK
            </button>
        `;

        return this.buildModalHtml({
            title: this.options.title,
            icon: this.options.icon,
            bodyHtml,
            footerHtml,
            size: 'modal-md'
        });
    }


    /**
     * Shows the information dialog (singleton - only one can be open at a time).
     * @param {Object} [options={}] - Configuration options
     * @param {string} [options.title='Information'] - Modal title
     * @param {string} [options.text=''] - Plain text content to display (escaped)
     * @param {string} [options.htmlUnsafe=''] - Untrusted HTML to sanitize and display
     * @param {string} [options.icon='bi-info-circle'] - Bootstrap icon class
     * @returns {Promise<null>} Resolves to null when closed
     */
    static async showInformationDialog(options = {}) {
        return BaseDialog.showSingleton(InformationDialog, options);
    }
}
